<script setup>
import { watch, ref, onMounted } from 'vue'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'

import StreamPlayer from '../ui/StreamPlayer.vue'
import FormButton from '../Atoms/FormButton.vue'
import IconBase from '../icon/IconBase.vue'
import IconEditPencil from '../icon/IconEditPencil.vue'
import { buildStreamPreviewSpecs } from '@/Utils/previewHost'
import MonitoringChart from '@/views/StreamsDash/MonitoringChart.vue'

const props = defineProps({
  stream: {
    type: Object,
    required: true,
    default() { return {} }
  },
  refLink: {
    type: Number,
    default: null,
    required: false
  },
  bitrateChart: {
    type: Boolean,
    default: false
  },
  playerType: {
    type: String,
    default: 'll'
  },
})

const emit = defineEmits({
  'on-source-stream-edit': () => true,
})

const streamPreviewSpecs = ref({})

// Media Pulse 
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

watch(() => mediaPulse.value, () => {
  streamPreviewSpecs.value = buildStreamPreviewSpecs(
    mediaPulse.value,
    props.stream?.key,
    props.stream
  )
})

onMounted(async () => {
  subscribeMediaPulse(props.stream)
})

watch(() => props.stream, () => {
  unsubscribeMediaPulse()
  subscribeMediaPulse(props.stream)
})

const onSourceStreamEdit = () => {
  emit('on-source-stream-edit', { stream: props.stream, refLink: props.refLink })
}

</script>

<template>
  <div class="relative h-full w-full">
    <template v-if="stream?.enabled">
      <div v-if="mediaPulse?.alive" class="multiview-preview-host absolute inset-0" :class="{
        'flex items-center justify-center': bitrateChart,
      }">
        <stream-player v-if="!bitrateChart && playerType === 'll'" :stream="stream" :stream-url="streamPreviewSpecs.url"
          :media-pulse="mediaPulse" :multiview="true" :refLink="refLink"
          class="absolute inset-0 h-full w-full bg-black" />

        <iframe v-else-if="!bitrateChart && playerType === 'embed'"
          :src="'https://player.castr.com/' + stream.key + '?disableChat=true'" class="embed video-frame absolute inset-0 h-full w-full" frameborder="0"
          width="100%" height="100%" scrolling="no" allowfullscreen webkitallowfullscreen mozallowfullscreen oallowfullscreen
          msallowfullscreen />

        <div v-else class="absolute inset-0 overflow-hidden p-3 pt-10">
          <div class="h-full w-full max-w-full">
            <MonitoringChart :streamKey="stream.key" :streamAlive="mediaPulse?.alive" :isAllowed="true" legendPosition="top"
              :tooltip="false" />
          </div>
        </div>
      </div>
      <div v-else class="absolute inset-0 flex items-center justify-center bg-darker">
        <p class="w-full text-center text-normal font-medium text-dark-7">
          Stream offline
        </p>
      </div>
    </template>
    <div v-else class="absolute inset-0 flex items-center justify-center bg-darker">
      <p class="w-full text-center text-normal font-medium text-dark-7">
        Stream disabled
      </p>
    </div>

    <!-- Overlay is always anchored to the tile root so edit stays inset consistently. -->
    <div class="pointer-events-none absolute inset-x-3 top-2 z-10 flex items-center justify-between gap-2">
      <span class="min-w-0 truncate text-sm font-medium leading-none rounded px-2 py-1 text-white">{{ (stream.name || '').substr(0, 40) }}</span>
      <span class="pointer-events-auto shrink-0">
        <FormButton
          type="light"
          is-icon
          size="md"
          class="btn_stream_edit"
          title="Edit stream"
          @click="onSourceStreamEdit"
        >
          <icon-base :width="16" :height="16" fill="none" class="text-white">
            <icon-edit-pencil />
          </icon-base>
        </FormButton>
      </span>
    </div>
  </div>
</template>
<style scoped>
.btn_stream_edit {
  @apply !rounded-full !bg-black/40 backdrop-blur-sm hover:!bg-black/55;
}

/* Host class lives in this SFC so scoped :deep() rules actually match StreamPlayer. */
.multiview-preview-host :deep(> div),
.multiview-preview-host :deep(> div > div) {
  width: 100%;
  height: 100%;
  position: relative;
}

.multiview-preview-host :deep(video) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
  border-radius: 0;
}
</style>
